import { createSlice, PayloadAction, PrepareAction } from '@reduxjs/toolkit'
import { PlaylistSort } from 'constants/enums'

interface Playlist {
  playlistid: string | number
  [key: string]: unknown
}

interface PlayListState {
  page: number
  totalItemsCount: number | null
  loading: boolean
  error: unknown
  releases: unknown[]
  playLists: Playlist[]
  playlistAbstracts: Playlist[]
  songsToAdd: unknown[]
  playlistUnderDisplay: Playlist | null
  playlistUnderEditing: Playlist | null
  searchToken: string
  sort: string
}

const initialState: PlayListState = {
  page: 0,
  totalItemsCount: null,
  loading: false,
  error: null,
  releases: [],
  playLists: [],
  playlistAbstracts: [],
  songsToAdd: [],
  playlistUnderDisplay: null,
  playlistUnderEditing: null,
  searchToken: '',
  sort: PlaylistSort.newest.value,
}

const playListSlice = createSlice({
  name: 'playList',
  initialState,
  reducers: {
    startLoadingPlaylist: (state) => {
      state.loading = true
    },
    setSongsToAdd: (state, action: PayloadAction<unknown[]>) => {
      state.songsToAdd = action.payload
    },
    setPlaylistAbstracts: (state, action: PayloadAction<Playlist[]>) => {
      state.playlistAbstracts = action.payload
    },
    loadingPlaylistSuccess: {
      prepare(playlists: Playlist[], page: number, totalItemsCount: number) {
        return { payload: { playlists, page, totalItemsCount } }
      },
      reducer(state, action: PayloadAction<{ playlists: Playlist[]; page: number; totalItemsCount: number }>) {
        const { playlists, page, totalItemsCount } = action.payload
        if (page === 1) {
          state.playLists = playlists
        } else if (page === state.page + 1) {
          state.playLists = state.playLists.concat(playlists)
        } else {
          return
        }
        state.page = page
        state.totalItemsCount = totalItemsCount
        state.loading = false
      },
    },
    loadingPlaylistFail: (state, action: PayloadAction<unknown>) => {
      state.error = action.payload
      state.loading = false
    },
    setPlaylists: (state, action: PayloadAction<Playlist[]>) => {
      state.playLists = action.payload
    },
    createPlayList: (state, action: PayloadAction<Playlist>) => {
      state.playLists.push({ playList: action.payload } as unknown as Playlist)
    },
    deletePlaylistStart: (state) => {
      state.loading = true
    },
    deletePlaylistSuccess: (state, action: PayloadAction<Playlist>) => {
      state.loading = false
      state.playLists = state.playLists.filter(
        (item) => item.playlistid !== action.payload.playlistid
      )
      state.playlistAbstracts = state.playlistAbstracts.filter(
        (item) => item.playlistid !== action.payload.playlistid
      )
    },
    deletePlaylistError: (state) => {
      state.loading = false
    },
    deleteSongFromPlaylist: {
      prepare(song: unknown, playlist: unknown) {
        return { payload: { song, playlist } }
      },
      reducer() {
        // deprecated/unused
      },
    },
    updatePlaylist: (state, action: PayloadAction<Playlist>) => {
      const playlist = action.payload
      state.playLists = state.playLists.map((item) =>
        item.playlistid === playlist.playlistid ? playlist : item
      )
      state.playlistAbstracts = state.playlistAbstracts.map((item) =>
        item.playlistid === playlist.playlistid ? playlist : item
      )
      if (state.playlistUnderDisplay?.playlistid === playlist.playlistid) {
        state.playlistUnderDisplay = playlist
      }
      if (state.playlistUnderEditing?.playlistid === playlist.playlistid) {
        state.playlistUnderEditing = playlist
      }
    },
    setPlaylistUnderDisplay: (state, action: PayloadAction<Playlist | null>) => {
      state.playlistUnderDisplay = action.payload
    },
    setPlaylistUnderEditing: (state, action: PayloadAction<Playlist | null>) => {
      state.playlistUnderEditing = action.payload
    },
    addTracksStart: (state) => {
      state.loading = true
    },
    addTracksSuccess: (state) => {
      state.loading = false
    },
    addTracksFail: (state) => {
      state.loading = false
    },
    renameStart: (state) => {
      state.loading = true
    },
    renameSuccess: (state, action: PayloadAction<Playlist>) => {
      state.loading = false
    },
    renameFail: (state) => {
      state.loading = false
    },
    updateLoadingPlaylist: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload
    },
    pinPlaylist: (state, action: PayloadAction<Playlist>) => {
      state.playLists = state.playLists.map((item) =>
        item.playlistid === action.payload.playlistid ? action.payload : item
      )
    },
    unpinPlaylist: (state, action: PayloadAction<Playlist>) => {
      state.playLists = state.playLists.map((item) =>
        item.playlistid === action.payload.playlistid ? action.payload : item
      )
    },
    updateSearchToken: (state, action: PayloadAction<string>) => {
      state.searchToken = action.payload
    },
    updateSort: (state, action: PayloadAction<string>) => {
      state.sort = action.payload
    },
    reset: () => initialState,
  },
})

export const playListActions = playListSlice.actions
export const playListReducer = playListSlice.reducer
export default playListActions
